import { Link } from "react-router-dom";
import { StatusBadge, TeamTag } from "./ui.js";
import { formatDateTime, gameSlot, recordLabel } from "../lib/format.js";

export interface GameSide {
  managerId: number | null;
  managerName: string | null;
  managerSlug: string | null;
  teamAbbr: string | null;
  teamName: string | null;
  score: number | null;
  seed: number | null;
  placeholder: string | null;
}

/** A competition record, keyed by manager id — the standings table's numbers. */
export type RecordOf = Map<number, { wins: number; losses: number; ties: number }>;

export interface GameLike {
  id: number;
  stage: string;
  status: string;
  matchupType: string | null;
  weekNumber: number | null;
  weekLabel: string | null;
  bracketLabel: string | null;
  bracketSide: string | null;
  bracketRound: number | null;
  bracketOrder: number | null;
  winnerToGameId: number | null;
  loserToGameId: number | null;
  scheduledFor: Date | string | null;
  playedAt?: Date | string | null;
  home: GameSide;
  away: GameSide;
  winnerManagerId: number | null;
}

function Side({
  side,
  isWinner,
  isFinal,
  align,
  records,
}: {
  side: GameSide;
  isWinner: boolean;
  isFinal: boolean;
  align: "left" | "right";
  records?: RecordOf;
}) {
  const name = side.managerName ?? side.placeholder ?? "TBD";
  // Before a competition kicks off every record is 0-0, which is a column of
  // noise rather than information — the same rule the manager view uses.
  const found = side.managerId != null ? records?.get(side.managerId) : undefined;
  const record = found && found.wins + found.losses + found.ties > 0 ? found : null;
  return (
    <div
      className={`flex min-w-0 flex-1 items-center gap-2 ${
        align === "right" ? "flex-row-reverse text-right" : ""
      }`}
    >
      <div className={`min-w-0 ${align === "right" ? "text-right" : ""}`}>
        <div
          className={`truncate font-semibold ${
            isFinal && !isWinner ? "text-chalk-400" : "text-chalk-50"
          }`}
        >
          {side.seed != null && (
            <span className="mr-1 text-xs text-chalk-500">#{side.seed}</span>
          )}
          {/* The row is itself a link to the game, so this must stay plain
              text: an <a> inside an <a> is invalid and browsers recover from
              it by splitting the outer link. */}
          {name}
        </div>
        {/* The record sits on the line under the name rather than beside it:
            the name line truncates, and on a phone it is the record that would
            be cut. This line is empty until a team is picked, so on an
            unplayed week it carries the record on its own. */}
        <div
          className={`mt-0.5 flex items-center gap-1.5 ${
            align === "right" ? "justify-end" : ""
          }`}
        >
          {record && (
            <span
              className="text-[11px] text-chalk-500"
              title="Their record in this competition"
            >
              {recordLabel(record.wins, record.losses, record.ties)}
            </span>
          )}
          <TeamTag abbr={side.teamAbbr} />
        </div>
      </div>
    </div>
  );
}

export default function GameRow({
  game,
  records,
}: {
  game: GameLike;
  /** Pass to show each manager's record beside their name. */
  records?: RecordOf;
}) {
  const isFinal = game.status === "final";
  const homeWin = isFinal && game.winnerManagerId === game.home.managerId;
  const awayWin = isFinal && game.winnerManagerId === game.away.managerId;
  // A finished game dims the side that lost. Nobody lost a tie, so a tie dims
  // neither — otherwise it reads as a game both managers somehow lost.
  const tied = isFinal && game.winnerManagerId == null && game.home.score != null;

  return (
    <Link
      to={`/games/${game.id}`}
      className="flex items-center gap-3 border-b border-field-800 px-4 py-3 last:border-b-0 hover:bg-field-850/60"
    >
      <div className="hidden w-24 shrink-0 text-xs text-chalk-500 sm:block">
        {gameSlot(game)}
        {game.matchupType && (
          <div className="mt-0.5 text-[11px] text-chalk-500">{game.matchupType}</div>
        )}
      </div>

      <Side
        side={game.away}
        isWinner={awayWin || tied}
        isFinal={isFinal}
        align="left"
        records={records}
      />

      <div className="shrink-0 px-2 text-center">
        {isFinal ? (
          <div className="font-mono text-lg font-bold tabular-nums">
            <span className={awayWin || tied ? "" : "text-chalk-400"}>
              {game.away.score}
            </span>
            <span className="mx-1.5 text-chalk-600">–</span>
            <span className={homeWin || tied ? "" : "text-chalk-400"}>
              {game.home.score}
            </span>
          </div>
        ) : (
          <div className="text-xs text-chalk-500">@</div>
        )}
        {!isFinal && (
          <div className="mt-1">
            <StatusBadge status={game.status} />
          </div>
        )}
        {!isFinal && game.scheduledFor && (
          <div className="mt-1 text-[11px] text-chalk-500">
            {formatDateTime(game.scheduledFor)}
          </div>
        )}
      </div>

      <Side
        side={game.home}
        isWinner={homeWin || tied}
        isFinal={isFinal}
        align="right"
        records={records}
      />
    </Link>
  );
}
